import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { QueryEntity } from '@datorama/akita';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';

import { Cmg1003BorrowerState, Cmg1003BorrowerStore } from './borrower.store';
import { DirtyChecker2 } from '../core/dirty-checker-2';
import { CreditReportType } from '../../cmg-1003/models/credit-request-types.enum';
import { DesktopUnderwritingSystemTypes } from '../../aus/models';

@Injectable({ providedIn: 'root' })
export class Cmg1003BorrowerQuery extends QueryEntity<Cmg1003BorrowerState> {
  protected store: Cmg1003BorrowerStore;

  static instance: Cmg1003BorrowerQuery;
  dirtyChecker2: DirtyChecker2 = new DirtyChecker2(this.store, this);
  constructor() {
    const store = inject(Cmg1003BorrowerStore);

    super(store);
    this.store = store;

    Cmg1003BorrowerQuery.instance = this;
  }

  /**
   * Use this function to determine if additional borrower is present or when not present
   * but LO is trying to add one.
   * NOTE: the conditional statement is temporary and may need fine tuning later.
   * TODO: Reuse logic from isAddBorrowerPresent
   * @param applicationId
   * @returns
   */
  isAdditionalBorrowerValid(applicationId: number) {
    return (
      this.hasEntity((b) => CmgUiUtilities.isIdTemporary(b.borrowerId)) ||
      !this.hasEntity(
        (e) =>
          CmgUiUtilities.isIdTemporary(e.borrowerId) &&
          e.applicationId === applicationId &&
          e.firstName !== null &&
          typeof e.firstName !== 'undefined',
      )
    );
  }

  allBorrowersHaveSsn$ = (): Observable<boolean> => {
    return this.selectAll().pipe(
      map((borrowers) => {
        return borrowers.filter((b) => b.borrowerId > 0).every((b) => !!b.ssn);
      }),
    );
  };

  allBorrowersHaveSameCreditReport(validReportTypes: CreditReportType[]): boolean {
    let allCreditRun = false;
    const reportsRun = [
      ...new Set(
        this.getAll()
          ?.filter((b) => b.borrowerId > 0)
          ?.map((b) => b.lastCreditReportType),
      ),
    ];
    if (reportsRun && reportsRun?.length === 1 && validReportTypes?.includes(reportsRun[0])) {
      allCreditRun = true;
    }
    return allCreditRun;
  }

  allBorrowersAusCreditValidations(system: DesktopUnderwritingSystemTypes): string[] | null {
    const validations: string[] = [];

    const allBorrowersHaveSoftCheck = this.allBorrowersHaveSameCreditReport([CreditReportType.SoftPull]);
    const allBorrowersHaveMerge = this.allBorrowersHaveSameCreditReport([CreditReportType.MergedCreditReport]);

    const messageDu = 'All applications must have Merge credit reports for DU';
    const messageLpa = 'All applications must have the same credit report type of SoftCheck or Merge for LPA';
    const messageEa = 'All applications must have SoftCheck credit reports for DU Early Assessment';
    const messageEaWithMerge = 'DU Early Assessment cannot be run with a Merge credit report. A SoftCheck credit report must be run.';

    let message = '';
    switch (system) {
      case DesktopUnderwritingSystemTypes.DU:
      case DesktopUnderwritingSystemTypes.Dual_DU_LPA:
        if (!allBorrowersHaveMerge) {
          message = messageDu;
        }
        break;
      case DesktopUnderwritingSystemTypes.LPA:
        if (!allBorrowersHaveMerge && !allBorrowersHaveSoftCheck) {
          message = messageLpa;
        }
        break;
      case DesktopUnderwritingSystemTypes.EA:
        if (!allBorrowersHaveSoftCheck) {
          message = messageEa;
        }
        if (allBorrowersHaveMerge) {
          message = messageEaWithMerge;
        }
        break;
      default:
        break;
    }

    if (message) {
      validations.push(message);
    }

    return validations?.length > 0 ? validations : null;
  }
}
